import React from 'react';
import ReactDOM from 'react-dom/client';
import 'hide-cra-error-overlay';
import reportWebVitals from './reportWebVitals';

import './index.css';
import 'semantic-ui-css/semantic.min.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { customStyles, customJavascript } from './lib/configuration';
import { init as telemetryInit, track as telemetryTrack } from './lib/telemetry';
import { getUser } from './lib/authentication';

import {
    createBrowserRouter,
    Outlet,
    RouterProvider,
} from 'react-router-dom';

import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TicketingEnabledGuard } from './components/TicketingEnabledGuard';
import { TicketingLanguageProvider } from './TicketingLanguageProvider';

import App from './App';
import EventList from './EventList';
import EventDetail from './EventDetail';
import ConferenceList from './ConferenceList';
import ConferenceDetail from './ConferenceDetail';
import Checkout from './Checkout';
import OrderConfirmation from './OrderConfirmation';
import Login from './Login';
import Register from './Register';
import MyTickets from './MyTickets';
import TicketDetail from './TicketDetail';
import TransferTicket from './TransferTicket';
import AcceptTransfer from './AcceptTransfer';
import Profile from './Profile';
import Memberships from './Memberships';
import Organiser from './Organiser';
import InstancePicker from './InstancePicker';
import DirectoryEventList from './DirectoryEventList';
import DirectoryOrganiser from './DirectoryOrganiser';

const TicketingLayout = () => (
    <>
        <Header />
        <div style={{ paddingTop: 62 }}>
            <TicketingEnabledGuard>
                <Outlet />
            </TicketingEnabledGuard>
        </div>
        <Footer />
    </>
);

const protect = (element) => <ProtectedRoute>{element}</ProtectedRoute>;

const slugRoutes = [
    { path: 'events', element: <EventList /> },
    { path: 'events/:eventId', element: <EventDetail /> },
    { path: 'conferences', element: <ConferenceList /> },
    { path: 'conferences/:conferenceId', element: <ConferenceDetail /> },
    { path: 'login', element: <Login /> },
    { path: 'register', element: <Register /> },
    { path: 'checkout', element: protect(<Checkout />) },
    { path: 'order-confirmation/:orderRef', element: protect(<OrderConfirmation />) },
    { path: 'my-tickets', element: protect(<MyTickets />) },
    { path: 'my-tickets/:orderRef', element: protect(<TicketDetail />) },
    { path: 'transfer/:orderRef', element: protect(<TransferTicket />) },
    { path: 'accept-transfer/:token', element: <AcceptTransfer /> },
    { path: 'profile', element: protect(<Profile />) },
    { path: 'memberships', element: protect(<Memberships />) },
    { path: 'organiser', element: <Organiser /> },
    { index: true, element: <App /> },
];

const router = createBrowserRouter([
    // Instance picker — no slug in path
    { path: '/', element: <InstancePicker /> },

    // Public event directory — cross-organiser, guest only. Gated on the
    // ticketing capability like every other ticketing route, but deliberately
    // NOT wrapped in TicketingLayout: that layout's Header/Footer build nav
    // links via buildPath(), which prefixes with the current /i/:instanceSlug
    // — meaningless here since these routes aren't scoped to a single
    // instance. TicketingEnabledGuard itself has no instance-param
    // dependency (no useParams(), just getCapabilities()), so it can wrap
    // these routes directly.
    {
        path: '/directory',
        element: <TicketingEnabledGuard><DirectoryEventList /></TicketingEnabledGuard>,
    },
    {
        path: '/directory/organisers/:slug',
        element: <TicketingEnabledGuard><DirectoryOrganiser /></TicketingEnabledGuard>,
    },

    // All app routes live under /i/:instanceSlug/
    {
        path: '/i/:instanceSlug',
        element: <TicketingLayout />,
        children: slugRoutes,
    },
]);

// Live preview: dashboard Ticketing Styling page sends colour overrides via postMessage
window.addEventListener('message', (event) => {
    if (event.data?.type === 'selwyn-style-preview') {
        const { colours, custom_css, custom_js } = event.data;
        const r = document.querySelector(':root');
        if (r && colours) {
            Object.entries(colours).forEach(([key, value]) => {
                if (value) r.style.setProperty(`--${key}`, value);
            });
        }
        if (custom_css !== undefined) {
            let styleEl = document.getElementById('selwyn-preview-css');
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = 'selwyn-preview-css';
                document.head.appendChild(styleEl);
            }
            styleEl.textContent = custom_css;
        }
        if (custom_js) {
            try {
                // eslint-disable-next-line no-new-func
                new Function(custom_js)();
            } catch (e) {
                console.error('[Selwyn preview] Custom JS error:', e);
            }
        }
    }
    if (event.data?.type === 'selwyn-bust-cache') {
        localStorage.removeItem('ticketing_configuration');
        localStorage.removeItem('ticketing_configurationTimestamp');
        window.location.reload();
    }
});

customStyles();

// Initialise telemetry and track every route change.
// The instance slug lives in /i/:instanceSlug/ path segments.
telemetryInit();
getUser().then(user => { if (user?.id) telemetryInit({ userId: String(user.id) }); }).catch(() => {});
router.subscribe((state) => {
    const pathname = state.location.pathname;
    const match = pathname.match(/^\/i\/([^/]+)/);
    telemetryInit({ tenantSlug: match ? match[1] : null });
    telemetryTrack('page_view', pathname);
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <TicketingLanguageProvider>
                <RouterProvider router={router} />
            </TicketingLanguageProvider>
        </QueryClientProvider>
    </React.StrictMode>
);

customJavascript();
reportWebVitals();
